import { supabase } from '../utils/supabase';
import { validateImageFile, generateLogoFilename, getFileExtension } from '../utils/imageUtils';

export interface UploadLogoResponse {
  success: boolean;
  message: string;
  logoUrl?: string;
  error?: Error;
}

export interface DeleteLogoResponse {
  success: boolean;
  message: string;
  error?: Error;
}

export class LogoService {
  private static readonly BUCKET_NAME = 'organization-logos';

  static async uploadLogo(file: File | Blob, organizationId: string): Promise<UploadLogoResponse> {
    try {
      // Validate file if it's a File object
      if (file instanceof File) {
        const validation = validateImageFile(file);
        if (!validation.isValid) {
          return {
            success: false,
            message: validation.error || 'Arquivo inválido'
          };
        }
      }

      // Generate filename
      const extension = file instanceof File ? getFileExtension(file.name) : 'png'; // Default for Blob

      const filename = generateLogoFilename(organizationId, extension);

      // Upload to Supabase Storage
      const { error } = await supabase.storage.from(this.BUCKET_NAME).upload(filename, file, {
        cacheControl: '3600',
        upsert: true
      });

      if (error) {
        throw new Error(`Erro no upload: ${error.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage.from(this.BUCKET_NAME).getPublicUrl(filename);

      return {
        success: true,
        message: 'Logo enviado com sucesso',
        logoUrl: urlData.publicUrl
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  static async deleteLogo(logoUrl: string): Promise<DeleteLogoResponse> {
    try {
      // Extract filename from URL
      const url = new URL(logoUrl);
      const pathParts = url.pathname.split('/');
      const filename = pathParts.slice(-2).join('/'); // organizationId/filename

      // Delete from Supabase Storage
      const { error } = await supabase.storage.from(this.BUCKET_NAME).remove([filename]);

      if (error) {
        throw new Error(`Erro ao remover logo: ${error.message}`);
      }

      return {
        success: true,
        message: 'Logo removido com sucesso'
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  static async updateOrganizationLogo(
    organizationId: string,
    logoUrl: string | null
  ): Promise<UploadLogoResponse> {
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ logo_url: logoUrl })
        .eq('id', organizationId);

      if (error) {
        throw new Error(`Erro ao atualizar organização: ${error.message}`);
      }

      return {
        success: true,
        message: 'Logo da organização atualizado com sucesso',
        logoUrl: logoUrl || undefined
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  static async replaceOrganizationLogo(
    organizationId: string,
    newFile: File | Blob,
    currentLogoUrl?: string
  ): Promise<UploadLogoResponse> {
    try {
      // Delete current logo if it exists
      if (currentLogoUrl) {
        const deleteResult = await this.deleteLogo(currentLogoUrl);
        if (!deleteResult.success) {
          console.warn('Falha ao remover logo anterior:', deleteResult.message);
          // Continue with upload even if deletion fails
        }
      }

      // Upload new logo
      const uploadResult = await this.uploadLogo(newFile, organizationId);
      if (!uploadResult.success) {
        return uploadResult;
      }

      // Update organization record
      const updateResult = await this.updateOrganizationLogo(organizationId, uploadResult.logoUrl!);

      if (!updateResult.success) {
        // If database update fails, try to clean up uploaded file
        if (uploadResult.logoUrl) {
          await this.deleteLogo(uploadResult.logoUrl);
        }
        return updateResult;
      }

      return {
        success: true,
        message: 'Logo da organização substituído com sucesso',
        logoUrl: uploadResult.logoUrl
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  static async removeOrganizationLogo(
    organizationId: string,
    currentLogoUrl: string
  ): Promise<DeleteLogoResponse> {
    try {
      // Remove from storage
      const deleteResult = await this.deleteLogo(currentLogoUrl);
      if (!deleteResult.success) {
        return deleteResult;
      }

      // Update organization record
      const updateResult = await this.updateOrganizationLogo(organizationId, null);
      if (!updateResult.success) {
        return {
          success: false,
          message: updateResult.message,
          error: updateResult.error
        };
      }

      return {
        success: true,
        message: 'Logo da organização removido com sucesso'
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }
}
